import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../supabase';

// Shown when reset email fails or times out – helps app owner fix Supabase config
const SUPABASE_EMAIL_CHECKLIST =
  'Reset email failed or timed out. In Supabase Dashboard: 1) Authentication → URL Configuration → add this exact Redirect URL: ' +
  (typeof window !== 'undefined' ? window.location.origin + '/reset-password' : 'https://app.shikhunitalian.com/reset-password') +
  ' 2) Authentication → SMTP: use custom SMTP (host, port, user, password, sender). 3) Check Authentication → Logs for the real error.';

const ForgotPasswordScreen: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    // Must match a URL in Supabase Dashboard → Authentication → URL Configuration → Redirect URLs
    const redirectUrl = `${window.location.origin}/reset-password`;
    const TIMEOUT_MS = 40000; // 40s - give SMTP time to respond

    try {
      const resetPromise = supabase.auth.resetPasswordForEmail(email, {
        redirectTo: redirectUrl,
      });
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('REQUEST_TIMEOUT')), TIMEOUT_MS)
      );
      const { data, error } = await Promise.race([resetPromise, timeoutPromise]);

      if (error) {
        const errorDetails = error as { status?: number; message?: string; name?: string };
        const errorStatus = errorDetails.status;
        const errorMsg = errorDetails.message || '';

        let userMessage: string;
        if (errorStatus === 504) {
          userMessage = SUPABASE_EMAIL_CHECKLIST;
        } else if (errorStatus === 503) {
          userMessage = 'The email service is temporarily unavailable. Please try again in a few minutes.';
        } else if (errorStatus === 500) {
          userMessage = SUPABASE_EMAIL_CHECKLIST;
        } else if (error.name === 'AuthRetryableFetchError' || errorStatus === 502) {
          userMessage = SUPABASE_EMAIL_CHECKLIST;
        } else if (errorMsg) {
          userMessage = errorMsg;
        } else {
          userMessage = `Something went wrong (${error.name || errorStatus || 'unknown'}). Please try again.`;
        }
        setMessage(userMessage);
      } else {
        setEmailSent(true);
        setMessage('Password reset email sent! Please check your inbox (and spam folder).');
      }
    } catch (err) {
      const isTimeout = err instanceof Error && err.message === 'REQUEST_TIMEOUT';
      setMessage(
        isTimeout ? SUPABASE_EMAIL_CHECKLIST : 'An unexpected error occurred. Please try again later.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-screen">
      <div className="auth-container">
        <div className="auth-header">
          <div className="logo">
            <img src="logo512.png" alt="Shikhun Italian" />
          </div>
          <h1>Reset Password</h1>
        </div>

        {!emailSent ? (
          <>
            <p className="text-center text-gray-600 mb-6">
              Enter your email address and we'll send you a link to reset your password.
            </p>

            <form onSubmit={handleResetPassword} className="auth-form">
              <div className="form-group">
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email address"
                  required
                />
              </div>

              {message && (
                <div className={`message ${message.includes('sent') ? 'success' : 'error'}`}>
                  {message}
                </div>
              )}

              <button 
                type="submit" 
                className="btn-primary"
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          </>
        ) : (
          <div className="text-center">
            <div className="text-6xl mb-4">📧</div>
            <h2 className="text-xl font-semibold mb-4 text-gray-800">Check Your Email</h2>
            <p className="text-gray-600 mb-6">
              We've sent a password reset link to <strong>{email}</strong>
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Click the link in the email to reset your password. The link will expire in 1 hour.
            </p>
            <button
              onClick={() => {
                setEmailSent(false);
                setEmail('');
                setMessage('');
              }}
              className="btn-primary mb-4"
            >
              Send Another Email
            </button>
          </div>
        )}

        <div className="auth-footer">
          <p><Link to="/login">← Back to Login</Link></p>
          <p>Don't have an account? <Link to="/register">Sign up</Link></p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordScreen;

